import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { Seo } from '@/components/Seo';
import { useProducts } from '@/contexts/ProductsContext';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/contexts/ToastContext';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { useWishlist } from '@/hooks/useWishlist';
import { trackAddToCart } from '@/lib/tracking';
import '../home/v2/home-v2.css';
import './wishlist.css';

// Wishlist (/wishlist): the products saved with the heart on the new Home,
// product pages and header. Uses the new Home's shop card styles.

const SET_SLUG = '3-step-set';
const toNumber = (price: string) => Number(price.replace(/[^0-9.]/g, '')) || 0;

const HEART = 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z';
const BAG = ['M5 8h14l-1 12H6z', 'M9 8V6a3 3 0 0 1 6 0v2'];

export default function WishlistPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { products, routineSet } = useProducts();
  const { addToCart, openCart } = useCart();
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();
  const wishlist = useWishlist();

  const all = [
    ...(routineSet
      ? [
          {
            slug: SET_SLUG,
            name: routineSet.designV2?.name || routineSet.name,
            href: prefix + '/3-step-treatment',
            price: routineSet.price,
            old: routineSet.originalPrice && toNumber(routineSet.originalPrice) > toNumber(routineSet.price) ? routineSet.originalPrice : '',
            img: routineSet.image,
            img2: '/v2/giftbox.webp',
          },
        ]
      : []),
    ...products.map((p) => ({
      slug: p.id,
      name: p.designV2?.name || p.name,
      href: `${prefix}/products/${p.id}`,
      price: p.price,
      old: '',
      img: p.image,
      img2: p.galleryImages[0] || p.image,
    })),
  ];
  const saved = wishlist.items.map((s) => all.find((a) => a.slug === s)).filter((a): a is (typeof all)[number] => !!a);
  const others = all.filter((a) => !wishlist.has(a.slug));

  const add = (it: (typeof all)[number]) => {
    addToCart(it.slug, 1);
    trackAddToCart({ id: it.slug, name: it.name, price: toNumber(it.price), quantity: 1 });
    showToast(t('cart.added', 'Added to Cart ✓'), t('cart.viewCart', 'View Cart →'), openCart);
  };
  const addAll = () => {
    saved.forEach((it) => addToCart(it.slug, 1));
    showToast(t('wishlist.addedAll', 'All saved items added to your cart'), t('cart.viewCart', 'View Cart →'), openCart);
  };

  const card = (it: (typeof all)[number], i: number) => (
    <article key={it.slug} className="pc2" style={{ ['--d' as string]: `${i * 0.05}s` }}>
      <div className="pim">
        <Link to={it.href} aria-label={it.name}>
          <img src={it.img} alt={it.name} loading="lazy" />
          <img src={it.img2} alt="" loading="lazy" />
        </Link>
        <div className="acts">
          <button
            type="button"
            className="ia wish"
            aria-pressed={wishlist.has(it.slug)}
            aria-label={wishlist.has(it.slug) ? `Remove ${it.name}` : `Save ${it.name}`}
            onClick={() => showToast(wishlist.toggle(it.slug) ? t('wishlist.saved', 'Saved to your wishlist') : t('wishlist.removed', 'Removed from your wishlist'))}
          >
            <svg viewBox="0 0 24 24">
              <path d={HEART} />
            </svg>
          </button>
          <button type="button" className="ia" aria-label={`Add ${it.name} to cart`} onClick={() => add(it)}>
            <svg viewBox="0 0 24 24">
              {BAG.map((d) => (
                <path key={d} d={d} />
              ))}
            </svg>
          </button>
        </div>
      </div>
      <h3>
        <Link to={it.href}>{it.name}</Link>
      </h3>
      <div className={`pprice${it.old ? ' on' : ''}`}>
        <b>{it.price}</b>
        {it.old && <s>{it.old}</s>}
      </div>
    </article>
  );

  return (
    <main className="w-full min-h-screen bg-white">
      <Seo title={`${t('wishlist.title', 'Wishlist')} · Rx.K Seoul`} description={t('wishlist.lede', 'The products you saved, ready when you are.')} noindex />
      <SharedNavbar />
      <div className="v2 wl" style={{ paddingTop: 'var(--v2h-h, 64px)' }}>
        <section className="sec">
          <div className="wrap">
            <div className="wlhead">
              <div className="sh" style={{ margin: 0, justifyItems: 'start', textAlign: 'left' }}>
                <span className="lbl">{t('wishlist.kicker', 'Your wishlist')}</span>
                <h2>
                  {t('wishlist.heading', 'Saved for')} <em>{t('wishlist.headingEm', 'later')}</em>
                </h2>
              </div>
              {saved.length > 0 && (
                <div className="wlbar">
                  <p>
                    <b>{saved.length}</b> {saved.length === 1 ? t('homeV2.shop.product', 'product') : t('homeV2.shop.products', 'products')}
                  </p>
                  <button type="button" className="btn wine" onClick={addAll}>
                    {t('wishlist.addAll', 'Add all to cart')}
                  </button>
                </div>
              )}
            </div>

            {saved.length > 0 ? (
              <div className="pgrid wlgrid" aria-live="polite">
                {saved.map(card)}
              </div>
            ) : (
              <div className="wlempty">
                <span className="wlic" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d={HEART} />
                  </svg>
                </span>
                <h3>{t('wishlist.emptyTitle', 'Nothing saved yet')}</h3>
                <p>{t('wishlist.emptyText', 'Tap the heart on any product to keep it here for later.')}</p>
                <Link className="btn wine" to={prefix + '/'}>
                  {t('wishlist.shop', 'Shop products')}
                </Link>
              </div>
            )}

            {!isAuthenticated && (
              <p className="wlnote">
                {t('wishlist.guestNote', 'Saved on this device.')} <Link to={prefix + '/login'}>{t('wishlist.signIn', 'Sign in to keep your wishlist on every device.')}</Link>
              </p>
            )}
          </div>
        </section>

        {others.length > 0 && (
          <section className="sec soft">
            <div className="wrap">
              <div className="sh">
                <span className="lbl">{t('wishlist.moreKicker', 'You may also like')}</span>
                <h2>
                  {t('wishlist.more', 'Complete your')} <em>{t('wishlist.moreEm', 'ritual')}</em>
                </h2>
              </div>
              <div className="pgrid wlgrid">{others.map(card)}</div>
            </div>
          </section>
        )}
      </div>
      <SharedFooter />
    </main>
  );
}
